import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import DateRangeIcon from '@mui/icons-material/DateRange';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { 
  SectionTitle, 
  AddressSection, 
  AddressTitle, 
  ReadOnlyTextField,
  ButtonContainer,
  SubmitButton 
} from './UserSettingsStyles';

const PersonalInfoTab = ({ 
  formData, 
  handleChange, 
  handleSubmit, 
  loading, 
  user, 
  passwordMismatch,
  getFormattedAddress 
}) => {
  return (
    <form onSubmit={handleSubmit}>
      <SectionTitle>
        <PersonIcon sx={{ mr: 1.5, verticalAlign: 'middle', color: 'primary.main' }} />
        פרטים אישיים
      </SectionTitle>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right', mb: 1 }}>
          אנא ודא שהפרטים שלך מעודכנים כדי שנוכל לספק לך את השירות הטוב ביותר
        </Typography>
      </Box>
      
      <Card 
        elevation={1} 
        sx={{ 
          mb: 3, 
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
            transform: 'translateY(-2px)'
          }
        }}
      >
        <Box sx={{ 
          borderBottom: '1px solid #f0f0f0', 
          bgcolor: 'rgba(25, 118, 210, 0.03)', 
          px: 2, 
          py: 1.5 
        }}>
          <Typography variant="subtitle1" fontWeight="medium" color="primary">
            פרטים בסיסיים
          </Typography>
        </Box>
        <CardContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="שם פרטי"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                variant="outlined"
                InputProps={{ 
                  style: { textAlign: "right" },
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="שם משפחה"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                variant="outlined"
                InputProps={{ 
                  style: { textAlign: "right" },
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="gender-label">מגדר</InputLabel>
                <Select
                  labelId="gender-label"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  label="מגדר"
                  required
                  sx={{ textAlign: "right" }}
                  startAdornment={
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <PersonIcon color="action" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value=""><em>בחר מגדר</em></MenuItem>
                  <MenuItem value="male">זכר</MenuItem>
                  <MenuItem value="female">נקבה</MenuItem>
                  <MenuItem value="other">אחר</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <AddressSection>
        <AddressTitle>
          <HomeIcon sx={{ mr: 1.5, fontSize: '1.1rem', verticalAlign: 'middle', color: 'primary.main' }} />
          כתובת מגורים
        </AddressTitle>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="רחוב"
              name="street"
              value={formData.street || ""}
              onChange={handleChange}
              variant="outlined"
              InputProps={{ 
                style: { textAlign: "right" },
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="רחוב המגורים">
                      <HomeIcon color="action" sx={{ fontSize: '1.1rem' }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="מספר בית"
              name="houseNumber"
              value={formData.houseNumber || ""}
              onChange={handleChange}
              variant="outlined"
              InputProps={{ 
                style: { textAlign: "right" },
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="מספר הבית">
                      <DateRangeIcon color="action" sx={{ fontSize: '1.1rem' }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="דירה (אופציונלי)"
              name="apartment"
              value={formData.apartment || ""}
              onChange={handleChange}
              variant="outlined"
              InputProps={{ 
                style: { textAlign: "right" },
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="מספר הדירה (אופציונלי)">
                      <InfoOutlinedIcon color="action" sx={{ fontSize: '1.1rem' }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="עיר"
              name="city"
              value={formData.city || ""}
              onChange={handleChange}
              variant="outlined"
              InputProps={{ 
                style: { textAlign: "right" },
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="עיר המגורים">
                      <LocationCityIcon color="action" sx={{ fontSize: '1.1rem' }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(25, 118, 210, 0.03)', borderRadius: '8px', border: '1px dashed rgba(25, 118, 210, 0.2)' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                כתובת מלאה כפי שתישמר במערכת:
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="primary">
                {getFormattedAddress() || 'אנא הזן את פרטי הכתובת'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </AddressSection>
        
      <Card elevation={0} sx={{ mt: 3, border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            מידע קשר
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ReadOnlyTextField
                fullWidth
                label="אימייל"
                value={user?.email || ""}
                variant="outlined"
                InputProps={{ 
                  readOnly: true,
                  style: { textAlign: "right", backgroundColor: "#f5f5f5" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyTextField
                fullWidth
                label="טלפון"
                value={user?.phone || ""}
                variant="outlined"
                InputProps={{ 
                  readOnly: true,
                  style: { textAlign: "right", backgroundColor: "#f5f5f5" }
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <ButtonContainer>
        <SubmitButton
          type="submit"
          variant="contained"
          disabled={loading || passwordMismatch}
        >
          שמור פרטים אישיים
        </SubmitButton>
      </ButtonContainer>
    </form>
  );
};

export default PersonalInfoTab; 